import assert from 'assert-ts';
import {produce} from 'immer';
import type {Thesaurus, ThesaurusNode} from '../types';

export const modifyNeoThesaurus = (thesaurus: Thesaurus): Thesaurus => {
  if (thesaurus.id !== 'neo') {
    return thesaurus;
  }

  return splitFictionalPeople(thesaurus);
};

/**
 * Splits the "Fiktive personer" (Fictional People) node into sub-nodes based on
 * the first letter of each child's label. We do this because there are too many
 * nodes to render at once.
 */
export const splitFictionalPeople = (thesaurus: Thesaurus): Thesaurus => {
  if (!thesaurus.children) {
    return thesaurus;
  }

  const fictionalPeopleNodeIndex = thesaurus.children.findIndex(
    c => c.code === 'https://id.nb.no/vocabulary/neo/11945', // Fiktive personer
  );

  if (fictionalPeopleNodeIndex === undefined || fictionalPeopleNodeIndex < 0) {
    assert.soft(
      false,
      'splitFictionalPeople: fictional people node not found in neo thesaurus',
    );
    return thesaurus;
  }

  const fictionalPeopleNode = thesaurus.children[fictionalPeopleNodeIndex];
  const children = produce(thesaurus.children, draft => {
    // Swap fictional people node with node of letter-children
    Object.assign(draft, {
      [fictionalPeopleNodeIndex]:
        splitChildrenOfThesaurusNodeByFirstLetter(fictionalPeopleNode),
    });
  });

  return {
    ...thesaurus,
    children,
  };
};

export const splitChildrenOfThesaurusNodeByFirstLetter = (
  node: ThesaurusNode,
): ThesaurusNode => {
  const letterMap: Record<string, ThesaurusNode> = {};

  (node.children ?? []).forEach(child => {
    const firstLetter = child.label[0] ? child.label[0].toUpperCase() : 'A';
    if (!letterMap[firstLetter]) {
      letterMap[firstLetter] = {
        id: `letter-${firstLetter}`,
        code: `letter-${firstLetter}`,
        label: firstLetter,
        selectable: false,
        deactivated: false,
        children: [],
      };
    }

    letterMap[firstLetter].children!.push(child);
  });

  return {
    ...node,
    children: Object.values(letterMap),
  };
};
